import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useForm } from "../useForm";

import InputTextOnly from "../../controls/InputTextOnly";
import ImportMediaDropzone from "../../importMedia/ImportMediaDropzone";
import type { CategoryFormValues, ProductCategory } from "../../../types/categories";

const initialFValues: CategoryFormValues = {
  name: "",
  is_default: false,
  image1: null,
};

interface ProductCategoryDetailsFormProps {
  category: ProductCategory | null;
  actionCall: (values: CategoryFormValues) => void | Promise<void>;
  isEdit: boolean;
}

export default function ProductCategoryDetailsForm(props: ProductCategoryDetailsFormProps) {
  const { category, actionCall, isEdit } = props;

  const validate = (fieldValues: Partial<CategoryFormValues> = values) => {
    let temp = { ...errors };

    temp.name = null;
    if (values.name === "") {
      temp.name = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange, resetForm } = useForm(
    initialFValues,
    false
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validate()) {
      actionCall(values);
    }
  };

  useEffect(() => {
    if (category) {
      setValues({ ...values, ...category });
    }
  }, [category, setValues]);

  const [image1, setImage1] = useState<string | null>(null);

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-4">
        <div className="space-y-3">
          <div>
            <div className="grid grid-cols-1 gap-4 max-w-4xl">
              <InputTextOnly
                label="Category name"
                name="name"
                onChange={handleInputChange}
                value={values.name ?? ""}
                labelOn={true}
                error={errors.name ?? undefined}
              />
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <label className="flex items-start gap-3" htmlFor="is_default">
                  <input
                    id="is_default"
                    name="is_default"
                    type="checkbox"
                    checked={Boolean(values.is_default)}
                    disabled={Boolean(isEdit && category?.is_default)}
                    onChange={(event) =>
                      handleInputChange({
                        target: { name: "is_default", value: event.target.checked },
                      })
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      Default category
                    </span>
                    <span className="mt-1 block text-sm text-gray-600">
                      Products imported without a QuickBooks category are assigned here.
                      {isEdit && category?.is_default
                        ? " Select another category as default before changing this one."
                        : " Selecting this replaces the tenant’s current default."}
                    </span>
                  </span>
                </label>
              </div>
              <div className="max-w-40">
                <div className="flex space-x-2">
                  <label htmlFor="image1" className="block text-sm font-medium text-gray-900">
                    Group image
                  </label>
                </div>
                {image1 ? (
                  <div>
                    <img src={image1} height="140" width="140" alt="New logo" />
                  </div>
                ) : isEdit && values.image1 ? (
                  <img
                    src={typeof values.image1 === "string" ? values.image1 : undefined}
                    alt={typeof values.image1 === "string" ? values.image1 : "Category"}
                    title={typeof values.image1 === "string" ? values.image1 : undefined}
                    className="media-image rounded-md"
                    height="140"
                    width="140"
                  />
                ) : null}
                <ImportMediaDropzone
                  fileName="image1"
                  handleInputChange={handleInputChange}
                  setUploadedImage={setImage1}
                />
              </div>
            </div>
          </div>

          {isEdit && (
            <div className="overflow-hidden card-custom">
              <div className="p-4">
                <p className="text-md font-semibold text-gray-900">Subcategories</p>
              </div>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-white">
                  <tr>
                    <th
                      scope="col"
                      className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900"
                    >
                      Image
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 text-right sm:pr-6">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {!category?.subcategories?.length ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="py-6 pl-4 pr-3 text-sm text-start sm:pl-6"
                        style={{ color: "var(--nav-text-secondary)" }}
                      >
                        No subcategories
                      </td>
                    </tr>
                  ) : (
                    category.subcategories.map((sub) => (
                      <tr key={sub.id}>
                        <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {sub.name}
                        </td>
                        <td className="py-3 pl-3 pr-3 text-sm">
                          {sub.image1 ? (
                            <img
                              src={sub.image1}
                              alt=""
                              className="h-10 w-10 object-cover rounded"
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <Link
                            to={`/sub-categories/${sub.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--nav-active-accent)] hover:opacity-90 bg-[var(--nav-active-accent)]"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="sm:flex-no-wrap">
            <div className="my-auto flex items-center">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
              >
                {isEdit ? "Save" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
